import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type {
  ProjectFieldErrors,
  Project,
  ProjectSpending,
  ProjectSpendingCapPayload
} from '@/stores/type/project.type'

import { useProjectUserStore } from '@/stores/projectUser'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { useAuthorizationStore } from '@/stores/authorization'
import { useBaseStore } from '@haskoning/vue-basestore'
import type { ProjectUserType } from '@/stores/type/projectUser.type'
import type { ProjectPermissionType } from '@/stores/type/authorization.type'

const { api } = useApi()

const baseUrl = API.CORE.PROJECTS.INDEX
const getProjectSpendingUrl = API.CORE.PROJECTS.SPENDING
const getProjectSpendingCapUrl = API.CORE.PROJECTS.SPENDING_CAP

export const useProjectStore = defineStore('project', () => {
  const baseStore = useBaseStore<Project>({ service: baseUrl, api })
  const authorizationStore = useAuthorizationStore()

  const servicePermissionProjects = ref<ProjectPermissionType[]>([])
  const filterFields = ref([
    'name',
    'location',
    'client',
    'description',
    'compute_spending_cap_eur',
    'storage_spending_cap_eur'
  ])
  const spending = ref<ProjectSpending | null>(null)
  const spendingLoading = ref(false)
  const spendingWriteLoading = ref(false)
  const projectFormErrors = ref<ProjectFieldErrors>({})

  const getCurrentProject = computed(() => {
    return baseStore.currentItem ? baseStore.currentItem : null
  })

  const hasAppProjectPermission = (permission: string) => {
    return authorizationStore.hasServicePermission({
      authSource: 'User',
      allowed: [permission]
    })
  }

  const getChangePermission = computed(() => (id: number) => {
    if (baseStore.items.value.find((project) => project.id === id)?.is_demo) return false
    if (hasAppProjectPermission('change_project')) return true
    const permissions =
      servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
    return permissions.includes('change')
  })

  const getDeletePermission = computed(() => (id: number) => {
    if (baseStore.items.value.find((project) => project.id === id)?.is_demo) return false
    if (hasAppProjectPermission('delete_project')) return true
    const permissions =
      servicePermissionProjects.value.find((item) => item.id === id)?.permissions ?? []
    return permissions.includes('delete')
  })

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value
      .filter((field) => filterFields.value.includes(field.parameter))
      .map((field) => {
        if (field.parameter === 'compute_spending_cap_eur') {
          return {
            ...field,
            label: 'Compute spending cap (EUR)',
            help_text:
              'Maximum estimated one-time compute spend before active calculations are cancelled.'
          }
        }
        if (field.parameter === 'storage_spending_cap_eur') {
          return {
            ...field,
            label: 'Storage spending cap (EUR/month)',
            help_text:
              'Maximum estimated monthly storage spend before active calculations are cancelled.'
          }
        }
        return field
      })
  })

  async function servicePermissionItems() {
    const projectUserStore = useProjectUserStore()
    const authStore = useDjangoAuthStore()

    const userId = authStore.user?.id
    await projectUserStore.fetchItems({ user: userId })
    const projectUserItems = projectUserStore.items || []

    const roleItems = projectUserItems.map((item: ProjectUserType) => {
      return {
        role: item.role,
        id: item.project
      }
    })
    servicePermissionProjects.value = await authorizationStore.getServicePermissionByRole(
      roleItems,
      ['change_project', 'delete_project']
    )
  }

  async function fetchSpending(projectId: number) {
    spendingLoading.value = true
    try {
      spending.value = (await api
        .service(getProjectSpendingUrl(projectId))
        .get()) as ProjectSpending
      return spending.value
    } catch (e) {
      console.error(e)
      spending.value = null
      return null
    } finally {
      spendingLoading.value = false
    }
  }

  async function updateSpendingCap(projectId: number, payload: ProjectSpendingCapPayload) {
    spendingWriteLoading.value = true
    try {
      spending.value = (await api.apiService.api(getProjectSpendingCapUrl(projectId), 'PATCH', {
        ...payload
      })) as ProjectSpending
      if (baseStore.currentItem.value?.id === projectId) {
        if (payload.compute_spending_cap_eur !== undefined) {
          baseStore.currentItem.value.compute_spending_cap_eur = payload.compute_spending_cap_eur
        }
        if (payload.storage_spending_cap_eur !== undefined) {
          baseStore.currentItem.value.storage_spending_cap_eur = payload.storage_spending_cap_eur
        }
      }
      return spending.value
    } catch (e) {
      console.error(e)
      return null
    } finally {
      spendingWriteLoading.value = false
    }
  }

  function clearProjectFormErrors(field?: string) {
    if (!field) {
      projectFormErrors.value = {}
      return
    }

    const nextErrors = { ...projectFormErrors.value }
    delete nextErrors[field]
    projectFormErrors.value = nextErrors
  }

  async function saveProjectForm(item: Project): Promise<Project | false> {
    clearProjectFormErrors()
    baseStore.writeLoading.value = true
    try {
      const service = api.service(baseUrl)
      const response = item.id ? await service.patch(item.id, item) : await service.create(item)

      if (!response || Array.isArray(response)) return false

      const existingIndex = baseStore.items.value.findIndex((project) => project.id === response.id)
      if (existingIndex >= 0) {
        const updatedItems = [...baseStore.items.value]
        updatedItems[existingIndex] = response
        baseStore.setItems(updatedItems)
      } else {
        baseStore.addItems([response])
      }
      baseStore.setCurrentItem(response)
      return response
    } catch (error) {
      projectFormErrors.value = await parseProjectFieldErrors(error)
      console.error(error)
      return false
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  async function parseProjectFieldErrors(error: unknown): Promise<ProjectFieldErrors> {
    if (!(error instanceof Response)) {
      return { detail: ['Saving the project failed. Please try again.'] }
    }

    try {
      const responseData: unknown = await error.json()
      if (typeof responseData === 'string') return { detail: [responseData] }
      if (!responseData || typeof responseData !== 'object') {
        return { detail: ['Saving the project failed. Please try again.'] }
      }

      return Object.fromEntries(
        Object.entries(responseData).map(([field, messages]) => [
          field,
          Array.isArray(messages) ? messages.map((message) => String(message)) : [String(messages)]
        ])
      )
    } catch {
      return { detail: ['Saving the project failed. Please try again.'] }
    }
  }

  function resetAll() {
    baseStore.reset()
    servicePermissionProjects.value = []
    spending.value = null
    spendingLoading.value = false
    spendingWriteLoading.value = false
    projectFormErrors.value = {}
  }

  return {
    ...baseStore,
    resetAll,
    getCurrentProject,
    getChangePermission,
    getDeletePermission,
    fieldsFiltered,
    servicePermissionItems,
    spending,
    spendingLoading,
    spendingWriteLoading,
    projectFormErrors,
    fetchSpending,
    updateSpendingCap,
    saveProjectForm,
    clearProjectFormErrors,
    filterFields
  }
})
